import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Shield,
  RotateCcw,
  Zap,
  GitMerge,
  ArrowRightLeft,
  DollarSign,
  Landmark,
  FileText,
  SkipForward,
  AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';
import {
  SkillStatus,
  RunStatus,
  periodDisplayName,
  type SkillExecution,
  type Period,
  type ConsolidationRun,
  type Entity,
} from '@/types';
import { consolidationApi, periodsApi, entitiesApi } from '@/api/client';
import ConsolidationReviewPanel from '@/components/ConsolidationReviewPanel';

/** Scope: full group */
const ALL_ENTITIES = '';
/** Run one consolidation per loaded period, oldest → newest */
const ALL_PERIODS = '__all_periods__';

async function waitForConsolidationRun(runId: string): Promise<RunStatus> {
  const terminal = new Set<RunStatus>([
    RunStatus.COMPLETED,
    RunStatus.APPROVED,
    RunStatus.FAILED,
    RunStatus.ROLLED_BACK,
  ]);
  for (let i = 0; i < 180; i++) {
    const res = await consolidationApi.get(runId);
    if (terminal.has(res.status)) return res.status;
    await new Promise((r) => setTimeout(r, 2000));
  }
  return RunStatus.FAILED;
}
import { useAppStore } from '@/store';

// --- Skill Icon Map ----------------------------------------------------------

const skillIcons: Record<string, React.ElementType> = {
  data_validation: Shield,
  ic_elimination: ArrowRightLeft,
  fx_translation: DollarSign,
  equity_consolidation: Landmark,
  generate_statements: FileText,
};

// --- Status Icon Component ---------------------------------------------------

function SkillStatusIcon({ status }: { status: SkillStatus }) {
  switch (status) {
    case SkillStatus.COMPLETED:
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    case SkillStatus.RUNNING:
      return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
    case SkillStatus.FAILED:
      return <XCircle className="w-5 h-5 text-red-500" />;
    case SkillStatus.SKIPPED:
      return <SkipForward className="w-5 h-5 text-gray-400" />;
    default:
      return <Clock className="w-5 h-5 text-gray-300" />;
  }
}

// --- Default skill steps (used before the run starts) ------------------------

const defaultSkills: SkillExecution[] = [
  { skill_name: 'data_validation', display_name: 'Data Validation', description: 'Validate trial balances, mappings, and FX rates', step_order: 1, status: SkillStatus.PENDING, progress_pct: 0, message: 'Waiting to start' },
  { skill_name: 'ic_elimination', display_name: 'IC Elimination', description: 'Eliminate intercompany transactions and balances', step_order: 2, status: SkillStatus.PENDING, progress_pct: 0, message: 'Waiting to start' },
  { skill_name: 'fx_translation', display_name: 'FX Translation', description: 'Translate foreign currency balances to reporting currency', step_order: 3, status: SkillStatus.PENDING, progress_pct: 0, message: 'Waiting to start' },
  { skill_name: 'equity_consolidation', display_name: 'Equity Consolidation', description: 'Consolidate equity and calculate minority interest', step_order: 4, status: SkillStatus.PENDING, progress_pct: 0, message: 'Waiting to start' },
  { skill_name: 'generate_statements', display_name: 'Generate Statements', description: 'Generate consolidated financial statements', step_order: 5, status: SkillStatus.PENDING, progress_pct: 0, message: 'Waiting to start' },
];

// --- Consolidation Page ------------------------------------------------------

export default function Consolidation() {
  const { addNotification } = useAppStore();

  const [periods, setPeriods] = useState<Period[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState(ALL_ENTITIES);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [batchHint, setBatchHint] = useState<string | null>(null);
  const [skills, setSkills] = useState<SkillExecution[]>(defaultSkills);
  const [runStatus, setRunStatus] = useState<RunStatus | null>(null);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [summaryMetrics, setSummaryMetrics] = useState<Record<string, Record<string, unknown>>>({});
  const [recentRuns, setRecentRuns] = useState<ConsolidationRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [consolidationTab, setConsolidationTab] = useState<'run' | 'review'>('run');

  // Load periods and recent runs
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [entRes, periodsRes, runsRes] = await Promise.all([
          entitiesApi.list({ page: 1, page_size: 500 }),
          periodsApi.list(),
          consolidationApi.list(),
        ]);
        const entItems = entRes.items ?? [];
        setEntities(
          entItems.map((e) => ({
            ...e,
            id: String(e.id),
          }))
        );
        const rawPeriods = Array.isArray(periodsRes) ? periodsRes : [];
        const normalizedPeriods: Period[] = rawPeriods.map((p) => ({
          ...p,
          id: String(p.id),
        }));
        setPeriods(normalizedPeriods);
        setRecentRuns(runsRes);
        const openPeriod = normalizedPeriods.find((p) => p.status === 'open') || normalizedPeriods[0];
        if (openPeriod) setSelectedPeriod(String(openPeriod.id));
      } catch {
        // Silent fail on initial load
      }
    };
    loadInitial();
  }, []);

  // Poll for run status
  const pollRunStatus = useCallback(async (runId: string) => {
    try {
      const result = await consolidationApi.get(runId);
      setRunStatus(result.status);
      if (result.skills?.length) {
        setSkills(result.skills);
      }
      if (result.summary) {
        // Convert summary to metrics display
        const metricsMap: Record<string, Record<string, unknown>> = {};
        for (const skill of (result.skills || [])) {
          if (skill.status === SkillStatus.COMPLETED) {
            // Use skill_outputs from the run if available
            metricsMap[skill.skill_name] = { status: 'completed' };
          }
        }
        if (result.summary) {
          setSummaryMetrics({ consolidation: result.summary as Record<string, unknown> });
        }
      }

      // If run is finished, stop polling and fetch journal entries
      if (
        result.status === RunStatus.COMPLETED ||
        result.status === RunStatus.FAILED ||
        result.status === RunStatus.APPROVED ||
        result.status === RunStatus.ROLLED_BACK
      ) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsRunning(false);

        if (result.status === RunStatus.COMPLETED || result.status === RunStatus.APPROVED) {
          addNotification({
            type: 'success',
            title: 'Consolidation Complete',
            message: `Consolidation run completed with ${result.summary?.total_journal_entries ?? 0} journal entries. Open Review for the consolidation matrix.`,
          });
          if (selectedPeriod !== ALL_PERIODS) {
            setConsolidationTab('review');
          }
        } else if (result.status === RunStatus.FAILED) {
          addNotification({
            type: 'error',
            title: 'Consolidation Failed',
            message: 'The consolidation run encountered errors. Review the results for details.',
          });
        }

        // Refresh recent runs
        try {
          const runsRes = await consolidationApi.list();
          setRecentRuns(runsRes);
        } catch {
          // Silent fail
        }
      }
    } catch {
      // Polling error, keep trying
    }
  }, [addNotification, selectedPeriod]);

  const buildRunParameters = useCallback((): Record<string, unknown> => {
    if (!selectedEntityId) return {};
    return { entity_ids: [Number(selectedEntityId)] };
  }, [selectedEntityId]);

  // Run consolidation
  const runConsolidation = useCallback(async () => {
    if (!selectedPeriod) return;
    if (
      selectedPeriod !== ALL_PERIODS &&
      !periods.some((p) => String(p.id) === String(selectedPeriod))
    ) {
      setError('Selected period is not in the list. Choose a period again.');
      return;
    }
    if (selectedPeriod === ALL_PERIODS && periods.length === 0) return;

    const singlePeriodId =
      selectedPeriod !== ALL_PERIODS ? Number(selectedPeriod) : NaN;
    if (
      selectedPeriod !== ALL_PERIODS &&
      (!Number.isFinite(singlePeriodId) || singlePeriodId <= 0)
    ) {
      setError('Invalid period. Choose another month.');
      return;
    }

    setIsRunning(true);
    setRunStatus(RunStatus.RUNNING);
    setSummaryMetrics({});
    setSkills(defaultSkills.map((s) => ({ ...s, status: SkillStatus.PENDING, progress_pct: 0, message: 'Waiting to start' })));
    setError(null);
    setBatchHint(null);

    const params = buildRunParameters();

    try {
      if (selectedPeriod === ALL_PERIODS) {
        const sorted = [...periods].sort((a, b) => a.year - b.year || a.month - b.month);
        const ok = window.confirm(
          `Run consolidation for all ${sorted.length} loaded periods in order (${periodDisplayName(sorted[0])} → ${periodDisplayName(sorted[sorted.length - 1])})? Each period runs the full pipeline; this may take several minutes.`
        );
        if (!ok) {
          setIsRunning(false);
          setRunStatus(null);
          return;
        }
        let lastRunId: string | null = null;
        let failed = false;
        for (let i = 0; i < sorted.length; i++) {
          const p = sorted[i];
          setBatchHint(`Period ${i + 1} of ${sorted.length}: ${periodDisplayName(p)}`);
          const run = await consolidationApi.run(String(p.id), { parameters: params });
          lastRunId = String(run.id);
          const st = await waitForConsolidationRun(lastRunId);
          if (st === RunStatus.FAILED || st === RunStatus.ROLLED_BACK) {
            failed = true;
            setError(`Consolidation failed during ${periodDisplayName(p)}`);
            break;
          }
        }
        setBatchHint(null);
        setCurrentRunId(lastRunId);
        if (lastRunId) await pollRunStatus(lastRunId);
        setIsRunning(false);
        setRunStatus(failed ? RunStatus.FAILED : RunStatus.COMPLETED);
        try {
          setRecentRuns(await consolidationApi.list());
        } catch {
          /* ignore */
        }
        return;
      }

      const run = await consolidationApi.run(String(selectedPeriod), { parameters: params });
      setCurrentRunId(String(run.id));
      setRunStatus(run.status as RunStatus);

      pollingRef.current = setInterval(() => {
        pollRunStatus(String(run.id));
      }, 2000);

      pollRunStatus(String(run.id));
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to start consolidation');
      setIsRunning(false);
      setRunStatus(RunStatus.FAILED);
      setBatchHint(null);
      addNotification({
        type: 'error',
        title: 'Consolidation Failed',
        message: apiErr.detail || 'Failed to start consolidation run.',
      });
    }
  }, [selectedPeriod, periods, buildRunParameters, pollRunStatus, addNotification]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleApprove = async () => {
    if (!currentRunId) return;
    try {
      await consolidationApi.approve(currentRunId);
      setRunStatus(RunStatus.APPROVED);
      addNotification({ type: 'success', title: 'Run Approved', message: 'Consolidation run has been approved.' });
      // Refresh recent runs
      const runsRes = await consolidationApi.list();
      setRecentRuns(runsRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Approve Failed', message: apiErr.detail || 'Failed to approve run.' });
    }
  };

  const handleRollback = async () => {
    if (!currentRunId) return;
    try {
      await consolidationApi.rollback(currentRunId);
      setRunStatus(null);
      setSkills(defaultSkills);
      setSummaryMetrics({});
      setCurrentRunId(null);
      addNotification({ type: 'info', title: 'Run Rolled Back', message: 'Consolidation run has been rolled back.' });
      // Refresh recent runs
      const runsRes = await consolidationApi.list();
      setRecentRuns(runsRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Rollback Failed', message: apiErr.detail || 'Failed to rollback run.' });
    }
  };

  const completedSteps = skills.filter((s) => s.status === SkillStatus.COMPLETED).length;
  const totalSteps = skills.length;
  const progressPct = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  const reviewPeriodId =
    selectedPeriod && selectedPeriod !== ALL_PERIODS ? selectedPeriod : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consolidation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Execute the AI-powered consolidation pipeline. If you pick one company, all of its active child and grandchild
            entities (via parent links) are included automatically—e.g. one holding company with four subsidiaries under it
            consolidates all four.
          </p>
          {batchHint && (
            <p className="text-sm text-blue-600 mt-2 font-medium" role="status">
              {batchHint}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 justify-end">
          <div className="flex items-center gap-2">
            <label htmlFor="consol-company" className="text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Company
            </label>
            <select
              id="consol-company"
              className="select-field text-sm min-w-[11rem] max-w-[14rem]"
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              disabled={isRunning}
              title="Choosing a parent company includes every active subsidiary under it (full org tree below that entity)."
            >
              <option value={ALL_ENTITIES}>All entities (group)</option>
              {[...entities]
                .filter((e) => e.is_active)
                .sort((a, b) => a.code.localeCompare(b.code))
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} — {e.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="consol-period" className="text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
              Period
            </label>
            <select
              id="consol-period"
              className="select-field text-sm min-w-[10rem] max-w-[12rem]"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              disabled={isRunning}
            >
              {periods.length === 0 && <option value="">Loading...</option>}
              {periods.length > 0 && (
                <option value={ALL_PERIODS}>All periods (sequential)</option>
              )}
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {periodDisplayName(p)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={runConsolidation}
            disabled={
              isRunning ||
              !selectedPeriod ||
              (selectedPeriod === ALL_PERIODS && periods.length === 0)
            }
            className="btn-primary"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Consolidation
              </>
            )}
          </button>
        </div>
      </div>

      {/* Run vs Review (review lives here, not in sidebar) */}
      <div className="flex gap-2 border-b border-gray-200" role="tablist" aria-label="Consolidation sections">
        <button
          type="button"
          role="tab"
          aria-selected={consolidationTab === 'run'}
          id="consol-tab-run"
          className={clsx(
            'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors min-h-[44px]',
            consolidationTab === 'run'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
          onClick={() => setConsolidationTab('run')}
        >
          Run
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={consolidationTab === 'review'}
          id="consol-tab-review"
          className={clsx(
            'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors min-h-[44px]',
            consolidationTab === 'review'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
          onClick={() => setConsolidationTab('review')}
        >
          Review
        </button>
      </div>

      {/* Error (pipeline only — avoids noise on Review tab) */}
      {error && consolidationTab === 'run' && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Consolidation Error</p>
            <p className="text-sm text-red-700 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {consolidationTab === 'review' && (
        <ConsolidationReviewPanel
          periodId={reviewPeriodId}
          scopeEntityId={selectedEntityId || undefined}
        />
      )}

      {consolidationTab === 'run' && (
        <>
      {/* Progress Bar */}
      <div className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Pipeline Progress</h2>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <span className="text-xs font-medium text-gray-500">
              {completedSteps} / {totalSteps} steps
            </span>
            {runStatus && (
              <span className={clsx(
                'badge',
                runStatus === RunStatus.COMPLETED && 'bg-green-50 text-green-700',
                runStatus === RunStatus.RUNNING && 'bg-blue-50 text-blue-700',
                runStatus === RunStatus.FAILED && 'bg-red-50 text-red-700',
                runStatus === RunStatus.APPROVED && 'bg-emerald-50 text-emerald-700',
              )}>
                {runStatus === RunStatus.APPROVED ? 'Approved' : runStatus.charAt(0).toUpperCase() + runStatus.slice(1)}
              </span>
            )}
            {runStatus === RunStatus.COMPLETED && currentRunId && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleRollback} className="btn-secondary text-sm py-1.5 min-h-[44px]">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Rollback
                </button>
                <button type="button" onClick={handleApprove} className="btn-success text-sm py-1.5 min-h-[44px]">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Approve All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-6">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              runStatus === RunStatus.FAILED ? 'bg-red-500' :
              runStatus === RunStatus.APPROVED ? 'bg-emerald-500' :
              'bg-primary-600',
            )}
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {skills.map((skill, index) => {
            const Icon = skillIcons[skill.skill_name] || Zap;
            return (
              <div
                key={skill.skill_name}
                className={clsx(
                  'flex items-center gap-4 px-4 py-3 rounded-lg border transition-all duration-300',
                  skill.status === SkillStatus.RUNNING && 'border-blue-200 bg-blue-50/50',
                  skill.status === SkillStatus.COMPLETED && 'border-green-100 bg-green-50/30',
                  skill.status === SkillStatus.FAILED && 'border-red-200 bg-red-50/50',
                  skill.status === SkillStatus.PENDING && 'border-gray-100 bg-white',
                )}
              >
                {/* Step number */}
                <div className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0',
                  skill.status === SkillStatus.COMPLETED && 'bg-green-100 text-green-700',
                  skill.status === SkillStatus.RUNNING && 'bg-blue-100 text-blue-700',
                  skill.status === SkillStatus.FAILED && 'bg-red-100 text-red-700',
                  skill.status === SkillStatus.PENDING && 'bg-gray-100 text-gray-400',
                )}>
                  {index + 1}
                </div>

                {/* Icon */}
                <Icon className={clsx(
                  'w-4.5 h-4.5 flex-shrink-0',
                  skill.status === SkillStatus.COMPLETED && 'text-green-500',
                  skill.status === SkillStatus.RUNNING && 'text-blue-500',
                  skill.status === SkillStatus.FAILED && 'text-red-500',
                  skill.status === SkillStatus.PENDING && 'text-gray-300',
                )} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={clsx(
                    'text-sm font-medium',
                    skill.status === SkillStatus.PENDING ? 'text-gray-400' : 'text-gray-900',
                  )}>
                    {skill.display_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{skill.description}</p>
                </div>

                {/* Status message */}
                <span className={clsx(
                  'text-xs font-medium',
                  skill.status === SkillStatus.COMPLETED && 'text-green-600',
                  skill.status === SkillStatus.RUNNING && 'text-blue-600',
                  skill.status === SkillStatus.FAILED && 'text-red-600',
                  skill.status === SkillStatus.PENDING && 'text-gray-400',
                )}>
                  {skill.message}
                </span>

                {/* Status icon */}
                <SkillStatusIcon status={skill.status} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Results section */}
      {Object.keys(summaryMetrics).length > 0 && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Consolidation Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(summaryMetrics).map(([key, metrics]) => (
              <div key={key} className="p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <GitMerge className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
                </div>
                <div className="space-y-1">
                  {Object.entries(metrics).map(([metricKey, value]) => (
                    <div key={metricKey} className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 capitalize">{metricKey.replace(/_/g, ' ')}</span>
                      <span className="font-medium text-gray-800">
                        {typeof value === 'number' ? value.toLocaleString() : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Runs */}
      {recentRuns.length > 0 && !isRunning && !runStatus && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Recent Consolidation Runs</h2>
          <div className="space-y-2">
            {recentRuns.slice(0, 5).map((run) => (
              <div
                key={run.id}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => {
                  setCurrentRunId(run.id);
                  setRunStatus(run.status);
                  // Load the run details
                  consolidationApi.get(run.id).then((result) => {
                    if (result.skills) setSkills(result.skills);
                    if (result.summary) setSummaryMetrics({ consolidation: result.summary as Record<string, unknown> });
                  }).catch(() => {});
                }}
              >
                <div className="flex items-center gap-3">
                  <GitMerge className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{run.period_name || run.period_id}</p>
                    <p className="text-xs text-gray-400">{new Date(run.started_at).toLocaleString()}</p>
                  </div>
                </div>
                <span className={clsx(
                  'badge',
                  run.status === RunStatus.COMPLETED && 'bg-green-50 text-green-700',
                  run.status === RunStatus.APPROVED && 'bg-emerald-50 text-emerald-700',
                  run.status === RunStatus.FAILED && 'bg-red-50 text-red-700',
                  run.status === RunStatus.RUNNING && 'bg-blue-50 text-blue-700',
                  run.status === RunStatus.ROLLED_BACK && 'bg-amber-50 text-amber-700',
                )}>
                  {run.status.charAt(0).toUpperCase() + run.status.slice(1).replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no run */}
      {!runStatus && !isRunning && recentRuns.length === 0 && (
        <div className="card py-16 text-center">
          <GitMerge className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Ready to Consolidate</h3>
          <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
            Select a period and use <span className="font-medium text-gray-700">Run Consolidation</span> above to execute
            the AI-powered consolidation pipeline. All five steps will be processed automatically.
          </p>
        </div>
      )}
        </>
      )}
    </div>
  );
}
